import { AccountApiTagTypes } from "@/modules/account/common/enum";
import { accountApi, useRenameAccountMutation } from "@/modules/account/services";
import { renameAccountValidationSchema } from "@/modules/account/validations";
import { Notification } from "@/shared/components";
import { useAppDispatch, useNotify } from "@/shared/hooks";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { TFormData } from "@ab.uitools/ui-library/types/globalTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  accountNumber?: string;
  name?: string;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}

export const AccountRenameDialog = ({
  accountNumber,
  name,
  isOpen,
  onClose,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showErrorAlertNotification } = useNotify();
  const [renameAccount, { isLoading }] = useRenameAccountMutation();
  const [accountName, setAccountName] = useState<string>(name || "");

  const handleSubmit = (data: TFormData, isDirty: boolean) => {
    if (isDirty && accountNumber) {
      renameAccount({
        accountNumber,
        data: { name: data.name }
      }).unwrap()
        .then(() => {
          refetch();
          dispatch(accountApi.util.invalidateTags([AccountApiTagTypes.AccountList]));
          onClose();
        })
        .catch((error) => {
          showErrorAlertNotification(error);
        });
    }
  };

  return (
    <Modal
      closeIcon
      closeOnOutsideClick
      titleProps={{
        title: t("accounts.renameTitle"),
        size: "large"
      }}
      isOpen={isOpen}
      onClose={onClose}
      className="rename-account-modal"
      onSubmit={() => {}}
      buttonProps={{
        confirm: {
          formId: "rename-account-form",
          buttonText: t("general.save"),
          buttonActionType: "submit",
          className: "rename-account-modal--submit-button",
          disabled: isLoading || accountName === name,
          isLoading: isLoading,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
    >
      <Notification className="mb-8"/>
      <FormContainer
        mode="onBlur"
        reValidateMode="onBlur"
        className="full-width"
        formId="rename-account-form"
        initialValues={{ name }}
        onSubmit={(data, formState) => handleSubmit(data, formState.isDirty)}
        validationScheme={renameAccountValidationSchema({ t })}
      >
        <FormField
          name="name"
          className="mb-32"
          errorMessageIcon="infoFilled"
          As={(props) => (
            <Input
              {...props}
              required
              autoComplete="name"
              onChange={(e) => setAccountName(e.target.value)}
              label={t("accounts.renameLabel")}
            />
          )}
        />
      </FormContainer>
    </Modal>
  );
};
